import {
  Button,
  Icons,
  Select,
  Sizes,
  TextInput,
  Toggle,
} from '@sede-enterprise/shell-ds-react-framework';
import { useEffect, useState } from 'react';
import { COMMON_MESSAGES } from 'src/constants/Common';
import {
  DELETE_TIME_SERIES_VIEW,
  GET_TIME_SERIES_VIEWS,
  SET_TIME_SERIES_VIEW,
  TIME_SERIES_VIEW_IDS,
} from 'src/constants/EUMarkets';
import { deleteApi, get, post } from 'src/services/GenesisApi';
import type { ICustomViewMapping } from 'src/types/EUMarkets.type';
import { Modal } from 'src/UI/Modal';
import { sendNotification } from 'src/UI/Notification';

type CustomView = {
  id: number;
  name: string;
  isPublic: boolean;
};

const NONE_VALUE = 'none';
const NONE_OPTION = { value: NONE_VALUE, label: 'None' };
const MAX_VIEW_NAME_LENGTH = 500;

const isSameViewName = (view: CustomView, name: string) =>
  view.name.trim().toLowerCase() === name.toLowerCase();

// An edited view keeps its id; a newly created view can only be identified by its unique name.
const findSavedView = (views: CustomView[], editingViewId: number | undefined, name: string) =>
  views.find((view) =>
    editingViewId === undefined ? isSameViewName(view, name) : view.id === editingViewId,
  );

type CustomViewSelectProps = {
  timeSeriesView: (typeof TIME_SERIES_VIEW_IDS)[keyof typeof TIME_SERIES_VIEW_IDS];
  readOnly?: boolean;
  label?: string;
  onCreateNew?: (selectAll?: boolean) => void;
  onEdit?: () => void;
  onCancelCreate?: () => void;
  hasSelectedNodes?: boolean;
  selectedMappings?: ICustomViewMapping[];
  onSaved?: () => void;
  onViewChange?: (id: number | null, forceReload?: boolean) => void;
};

const CustomViewSelect = ({
  timeSeriesView,
  readOnly = false,
  label = 'Custom View',
  onCreateNew,
  onEdit,
  onCancelCreate,
  hasSelectedNodes = false,
  selectedMappings = [],
  onSaved,
  onViewChange,
}: CustomViewSelectProps) => {
  const [views, setViews] = useState<CustomView[]>([]);
  const [selectedViewId, setSelectedViewId] = useState<number | string>(NONE_VALUE);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingView, setEditingView] = useState<CustomView | null>(null);
  const [viewName, setViewName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingView, setDeletingView] = useState<CustomView | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setViews([]);
    setSelectedViewId(NONE_VALUE);
    const query = new URLSearchParams({ timeSeriesView: String(timeSeriesView) });

    get(`${GET_TIME_SERIES_VIEWS}?${query.toString()}`)
      .then((response) => {
        if (active) {
          setViews(response.data ?? []);
        }
      })
      .catch(() => {
        if (active) {
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [timeSeriesView]);

  const refreshViewsAfterSave = (editingViewId: number | undefined, savedName: string) => {
    setIsLoading(true);
    const query = new URLSearchParams({ timeSeriesView: String(timeSeriesView) });
    return get(`${GET_TIME_SERIES_VIEWS}?${query.toString()}`)
      .then((response) => {
        const refreshedViews = (response.data ?? []) as CustomView[];
        setViews(refreshedViews);
        const savedView = findSavedView(refreshedViews, editingViewId, savedName);
        setSelectedViewId(savedView?.id ?? NONE_VALUE);
        onViewChange?.(savedView?.id ?? null, true);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: 'View saved, but custom views could not be refreshed.',
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleSave = () => {
    if (isSaving) {
      return;
    }
    const trimmedName = viewName.trim();
    let message = '';

    if (!trimmedName) {
      message = 'View name is required.';
    } else if (viewName.length > MAX_VIEW_NAME_LENGTH) {
      message = 'View name must be 500 characters or fewer.';
    } else if (
      views.some((view) => view.id !== editingView?.id && isSameViewName(view, trimmedName))
    ) {
      message = 'View name must be unique.';
    } else if (!hasSelectedNodes || selectedMappings.length === 0) {
      message = 'Select at least one node to save the view.';
    }

    if (message) {
      sendNotification({ type: 'error', message });
      return;
    }

    setIsSaving(true);
    post(SET_TIME_SERIES_VIEW, {
      id: editingView?.id ?? 0,
      viewId: timeSeriesView,
      name: trimmedName,
      isPublic,
      mappings: selectedMappings,
    })
      .then(() => {
        sendNotification({ type: 'success', message: 'Custom view saved successfully.' });
        setViewName('');
        setIsPublic(false);
        setIsCreating(false);
        setEditingView(null);
        onSaved?.();
        return refreshViewsAfterSave(editingView?.id, trimmedName);
      })
      .catch(() => {
        sendNotification({ type: 'error', message: 'Failed to save custom view.' });
      })
      .finally(() => setIsSaving(false));
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setDeletingView(null);
    }
  };

  const handleDelete = () => {
    if (!deletingView || isDeleting) {
      return;
    }

    setIsDeleting(true);
    const query = new URLSearchParams({ customViewId: String(deletingView.id) });
    deleteApi(`${DELETE_TIME_SERIES_VIEW}?${query.toString()}`, {})
      .then(() => {
        setViews((previous) => previous.filter((view) => view.id !== deletingView.id));
        setSelectedViewId(NONE_VALUE);
        setDeletingView(null);
        onViewChange?.(null, true);
        sendNotification({ type: 'success', message: 'Custom view deleted successfully.' });
      })
      .catch(() => {
        sendNotification({ type: 'error', message: 'Failed to delete custom view.' });
      })
      .finally(() => setIsDeleting(false));
  };

  if (isCreating || editingView !== null) {
    return (
      <div className='filter-field custom-view-filter-field'>
        <span className='filter-label'>Custom View</span>
        <div className='custom-view-controls'>
          <TextInput
            size={Sizes.Small}
            aria-label='Custom view name'
            placeholder='View name'
            value={viewName}
            onChange={(event) => setViewName(event.target.value)}
            data-testid='custom-view-name'
            disabled={isSaving}
          />
          <Toggle
            size={Sizes.Medium}
            label='Public'
            aria-label='Public'
            checked={isPublic}
            onChange={(checked) => setIsPublic(checked)}
            wrapperProps={{ className: 'custom-view-public' }}
            data-testid='custom-view-public'
            disabled={isSaving}
          />
          <Button
            size='small'
            variant='outlined'
            data-testid='custom-view-cancel'
            disabled={isSaving}
            onClick={() => {
              setViewName('');
              setIsPublic(false);
              setIsCreating(false);
              setEditingView(null);
              onCancelCreate?.();
            }}
          >
            Cancel
          </Button>
          <Button
            size='small'
            data-testid='custom-view-save'
            onClick={handleSave}
            loading={isSaving}
            disabled={isLoading || isSaving}
          >
            {editingView ? 'Update' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='filter-field custom-view-filter-field'>
      <span className='filter-label'>{label}</span>
      <div className='custom-view-controls'>
        <Select
          size={Sizes.Small}
          aria-label={label}
          options={[NONE_OPTION, ...views.map((view) => ({ value: view.id, label: view.name }))]}
          optionLabelProp='label'
          optionFilterProp='label'
          allowClear={false}
          value={selectedViewId}
          onChange={(value) => {
            setSelectedViewId(value as number | string);
            onViewChange?.(value === NONE_VALUE ? null : Number(value));
          }}
          disabled={isLoading || deletingView !== null}
          data-testid='custom-view-select'
        />
        {!readOnly && (
          <>
            <Button
              icon={<Icons.Edit />}
              iconOnly
              size='small'
              variant='filled'
              aria-label='Edit custom view'
              title='Edit custom view'
              disabled={isLoading || selectedViewId === NONE_VALUE || deletingView !== null}
              data-testid='custom-view-edit'
              onClick={() => {
                const view = views.find((candidate) => candidate.id === selectedViewId);
                if (!view) {
                  return;
                }
                setEditingView(view);
                setViewName(view.name);
                setIsPublic(view.isPublic ?? false);
                onEdit?.();
              }}
            />
            <Button
              icon={<Icons.Trash />}
              iconOnly
              size='small'
              variant='filled'
              aria-label='Delete custom view'
              title='Delete custom view'
              disabled={isLoading || selectedViewId === NONE_VALUE || deletingView !== null}
              data-testid='custom-view-delete'
              onClick={() => {
                const view = views.find((candidate) => candidate.id === selectedViewId);
                if (view) {
                  setDeletingView(view);
                }
              }}
            />
            <Button
              icon={<Icons.Add />}
              iconPosition='right'
              size='small'
              variant='outlined'
              data-testid='custom-view-create'
              disabled={isSaving || deletingView !== null}
              onClick={() => {
                setViewName('');
                setEditingView(null);
                setIsPublic(false);
                setIsCreating(true);
                onCreateNew?.(selectedViewId !== NONE_VALUE);
              }}
            >
              Create New
            </Button>
          </>
        )}
      </div>
      <Modal
        open={deletingView !== null}
        title='Delete Custom View'
        onClose={closeDeleteModal}
        closable={!isDeleting}
        maskClosable={false}
        width={480}
        footer={
          <div
            className='button-style'
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 10 }}
          >
            <Button variant='outlined' onClick={closeDeleteModal} disabled={isDeleting}>
              Cancel
            </Button>
            <Button onClick={handleDelete} loading={isDeleting} disabled={isDeleting}>
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this custom view?</p>
        <p>
          <strong>View Name:</strong> {deletingView?.name}
        </p>
      </Modal>
    </div>
  );
};

export default CustomViewSelect;
